<!-- Progress bar used inside the featured slot -->
<script setup>
import { computed, ref, watch, onMounted } from 'vue'

const props = defineProps({
    pct: { type: Number, required: true },
    n:   { type: Number, required: true },
    g:   { type: Number, required: true },
    tone:  { type: String, default: 'gold' },
    label: { type: String, default: 'stars' },
})

const TONES = {
    gold:   ['#FCD34D', '#FBBF24'],
    rose:   ['#F43F5E', '#FB7185'],
    blue:   ['#0052CC', '#4D94FF'],
    violet: ['#8B5CF6', '#C4B5FD'],
}

const displayN = ref(0)

function easeOutExpo(t) {
    return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)
}

function animateTo(from, to, duration) {
    const start = performance.now()
    function step(now) {
        const t = Math.min((now - start) / duration, 1)
        displayN.value = Math.round(from + (to - from) * easeOutExpo(t))
        if (t < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
}

onMounted(() => animateTo(0, props.n, 1200))

watch(() => props.n, (newVal, oldVal) => animateTo(oldVal ?? 0, newVal, 900))

const displayPct = computed(() => {
    if (!props.g) return 0
    return Math.min(Math.round((displayN.value / props.g) * 100), 100)
})

const fillStyle = computed(() => {
    const [a, b] = TONES[props.tone] || TONES.gold
    return {
        width: displayPct.value + '%',
        background: `linear-gradient(90deg, ${a}, ${b})`,
        boxShadow: `0 0 18px ${a}80`,
    }
})
</script>

<template>
    <div class="fp">
        <div class="fp__row">
            <span class="fp__n">{{ displayN.toLocaleString() }}</span>
            <span class="fp__g">/ {{ g.toLocaleString() }} {{ label }} · {{ displayPct }}%</span>
        </div>
        <div class="fp__bar">
            <div class="fp__fill" :style="fillStyle" />
        </div>
    </div>
</template>

<style scoped>
.fp { margin: 4px 0 8px; }
.fp__row { display: flex; align-items: baseline; gap: 12px; margin-bottom: 10px; }
.fp__n { font-family: 'Manrope', sans-serif; font-size: 36px; font-weight: 900; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }
.fp__g { color: #eaeaea; font-size: 16px; }
.fp__bar { height: 10px; background: rgba(255,255,255,0.10); border-radius: 9999px; overflow: hidden; max-width: 540px; }
.fp__fill { height: 100%; border-radius: 9999px; }
</style>
